// ApiDocsPage.tsx
import React, { useState } from "react";
import { type ApiEndpoint } from "./apiDocsSchema";
import ApiSidebar from "./components/ApiSidebar";
import ApiPlayground from "./components/ApiPlayground";
import HeaderApi from "../../components/HeaderApi";

const ApiDocsPage: React.FC = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(
    null
  );

  // helper pra separar obrigatórios e opcionais
  const getRequiredAndOptionalParams = (ep: ApiEndpoint | null) => {
    const all = ep?.params ?? [];
    const required = all.filter((p) => p.required);
    const optional = all.filter((p) => !p.required);
    return { required, optional };
  };

  const { required, optional } = getRequiredAndOptionalParams(selectedEndpoint);

  const renderExampleValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
  };

  // Exemplo de resposta vindo do endpoint (para DETALHAMENTO DA RESPOSTA)
  const exampleResponseRaw = selectedEndpoint?.examples?.find(
    (ex) => ex.response
  )?.response;
  const exampleResponseText = exampleResponseRaw
    ? JSON.stringify(exampleResponseRaw, null, 2)
    : "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        fontFamily: "system-ui",
      }}
    >
      {/* HEADER FIXO NO TOPO */}
      <HeaderApi />

      {/* Conteúdo da página de documentação em grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px minmax(0, 1.6fr) minmax(0, 1.2fr)",
          flex: 1,
          minHeight: 0,
          // 👇 empurra o conteúdo para baixo do header fixo (54px)
          marginTop: 54,
          boxSizing: "border-box",
        }}
      >
        {/* ========== Sidebar de Navegação (componente separado) ========== */}
        <ApiSidebar
          selectedEndpoint={selectedEndpoint}
          onSelectEndpoint={setSelectedEndpoint}
        />

        {/* ========== Coluna central: documentação textual do endpoint ========== */}
        <main
          style={{
            padding: "24px 100px",
            overflowY: "auto",
            background: "#f9fafb",
            boxSizing: "border-box",
          }}
        >
          {!selectedEndpoint && (
            <div style={{ color: "#6b7280", fontSize: 14 }}>
              Selecione um endpoint ao lado para ver detalhes.
            </div>
          )}

          {selectedEndpoint && (
            <section>
              {/* Cabeçalho com nome da rota, path e método */}
              <header
                style={{
                  marginBottom: 28,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 400,
                    color: "#14161ABE",
                  }}
                >
                  {selectedEndpoint.name}
                </div>

                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 14,
                    color: "#374151",
                    marginTop: 4,
                  }}
                >
                  {selectedEndpoint.method} {selectedEndpoint.path}
                </div>

                {selectedEndpoint.authRequired && (
                  <span
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid #e5e7eb",
                      color: "#4b5563",
                      background: "#f9fafb",
                      width: "fit-content",
                    }}
                  >
                    Auth necessária
                  </span>
                )}
              </header>

              {/* Descrição geral da rota */}
              <section style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 14,
                    color: "#4b5563",
                    lineHeight: 1.6,
                  }}
                >
                  {selectedEndpoint.description ? (
                    selectedEndpoint.description
                  ) : (
                    <>
                      Esta rota{" "}
                      <span style={{ fontWeight: 600 }}>
                        {selectedEndpoint.method} {selectedEndpoint.path}
                      </span>{" "}
                      faz parte da API do sistema e pode ser utilizada conforme
                      a documentação ao lado. Utilize o playground para testar
                      requisições em tempo real.
                    </>
                  )}
                </div>
              </section>

              {/* Rota na qual é utilizada (estilo igual ARGUMENTOS) */}
              <section style={{ marginTop: 24, marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#8b8c8e",
                  }}
                >
                  Rota na qual é utilizada
                </div>
                <div
                  style={{
                    marginTop: 6,
                    marginBottom: 16,
                    height: 1,
                    backgroundColor: "#8b8c8e",
                    opacity: 0.35,
                  }}
                />

                <div style={{ marginLeft: 16 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#4b5563",
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedEndpoint.usageDescription ? (
                      selectedEndpoint.usageDescription
                    ) : (
                      <>
                        Esta rota é utilizada dentro do módulo{" "}
                        <span style={{ fontWeight: 600 }}>
                          {selectedEndpoint.group || "Geral"}
                        </span>{" "}
                        para operações relacionadas a{" "}
                        <span style={{ fontWeight: 500 }}>
                          {selectedEndpoint.name}
                        </span>
                        . Pode ser chamada a partir do frontend EVA, integrações
                        externas ou automações internas que dependam deste
                        recurso.
                      </>
                    )}
                  </div>
                </div>
              </section>

              {/* ARGUMENTOS da rota */}
              {selectedEndpoint.params &&
                selectedEndpoint.params.length > 0 && (
                  <section style={{ marginTop: 40, marginBottom: 24 }}>
                    {/* Cabeçalho ARGUMENTOS */}
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#8b8c8e",
                      }}
                    >
                      ARGUMENTOS
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        marginBottom: 16,
                        height: 1,
                        backgroundColor: "#8b8c8e",
                        opacity: 0.35,
                      }}
                    />

                    <div style={{ marginLeft: 16 }}>
                      {/* Primeiro os obrigatórios */}
                      {required.map((p) => {
                        const statusLabel = "Obrigatório";
                        const badgeBg = "#B73434FF";
                        const badgeText = "#374151";
                        return (
                          <div
                            key={`req-${p.in}-${p.name}`}
                            style={{ marginBottom: 18 }}
                          >
                            {/* Nome do parâmetro */}
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#111827",
                              }}
                            >
                              {p.name}
                            </div>

                            {/* Linha com OBRIGATÓRIO + tipo/local */}
                            <div
                              style={{
                                marginTop: 4,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                  padding: "2px 0px",
                                  color: badgeBg,
                                  letterSpacing: "0.06em",
                                  fontWeight: 600,
                                }}
                              >
                                {statusLabel}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#6b7280",
                                  fontFamily: "monospace",
                                }}
                              >
                                {p.in}, {p.type}
                              </span>
                            </div>

                            {/* Descrição */}
                            {p.description && (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 13,
                                  color: "#4b5563",
                                  lineHeight: 1.6,
                                }}
                              >
                                {p.description}
                              </div>
                            )}

                            {/* Exemplo */}
                            {p.example !== undefined && (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "#4b5563",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#7B7B7BFF",
                                  }}
                                >
                                  Exemplo:
                                </span>{" "}
                                <code
                                  style={{
                                    fontFamily: "monospace",
                                    background: "#e5e7eb",
                                    padding: "1px 4px",
                                    borderRadius: 4,
                                  }}
                                >
                                  {renderExampleValue(p.example)}
                                </code>
                              </div>
                            )}

                            {/* Origem */}
                            {p.source && (
                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: 12,
                                  color: "#6b7280",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#7B7B7BFF",
                                  }}
                                >
                                  Origem:
                                </span>{" "}
                                {p.source}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Depois os opcionais */}
                      {optional.map((p) => {
                        const statusLabel = "OPCIONAL";
                        const badgeText = "#374151";
                        return (
                          <div
                            key={`opt-${p.in}-${p.name}`}
                            style={{ marginBottom: 18 }}
                          >
                            {/* Nome do parâmetro */}
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#111827",
                              }}
                            >
                              {p.name}
                            </div>

                            {/* Linha com OPCIONAL + tipo/local */}
                            <div
                              style={{
                                marginTop: 4,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  textTransform: "uppercase",
                                  fontSize: 10,
                                  borderRadius: 0,
                                  color: "#909B18FF",
                                  letterSpacing: "0.06em",
                                  fontWeight: 600,
                                }}
                              >
                                {statusLabel}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#6b7280",
                                  fontFamily: "monospace",
                                }}
                              >
                                {p.in}, {p.type}
                              </span>
                            </div>

                            {/* Descrição */}
                            {p.description && (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 13,
                                  color: "#4b5563",
                                  lineHeight: 1.6,
                                }}
                              >
                                {p.description}
                              </div>
                            )}

                            {/* Exemplo */}
                            {p.example !== undefined && (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "#4b5563",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#6b7280",
                                  }}
                                >
                                  Exemplo:
                                </span>{" "}
                                <code
                                  style={{
                                    fontFamily: "monospace",
                                    background: "#e5e7eb",
                                    padding: "1px 4px",
                                    borderRadius: 4,
                                  }}
                                >
                                  {renderExampleValue(p.example)}
                                </code>
                              </div>
                            )}

                            {/* Origem */}
                            {p.source && (
                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: 12,
                                  color: "#6b7280",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#6b7280",
                                  }}
                                >
                                  Origem:
                                </span>{" "}
                                {p.source}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

              {/* DETALHAMENTO DA RESPOSTA (sempre abaixo dos ARGUMENTOS) */}
              <section style={{ marginTop: 40, marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#8b8c8e",
                  }}
                >
                  DETALHAMENTO DA RESPOSTA
                </div>
                <div
                  style={{
                    marginTop: 6,
                    marginBottom: 16,
                    height: 1,
                    backgroundColor: "#8b8c8e",
                    opacity: 0.35,
                  }}
                />

                <div style={{ marginLeft: 16 }}>
                  {selectedEndpoint.responseDetails || exampleResponseText ? (
                    <>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#4b5563",
                          lineHeight: 1.6,
                          marginBottom: 8,
                        }}
                      >
                        {selectedEndpoint.responseDetails ? (
                          selectedEndpoint.responseDetails
                        ) : (
                          <>
                            Abaixo um exemplo de payload retornado por esta
                            rota, conforme implementado atualmente:
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                        lineHeight: 1.6,
                      }}
                    >
                      Ainda não há um detalhamento de resposta cadastrado para
                      esta rota. Verifique o playground ao lado para ver a
                      resposta em tempo real da API.
                    </div>
                  )}
                </div>
              </section>
            </section>
          )}
        </main>

        {/* ========== Coluna direita: Playground (componente separado) ========== */}
        <ApiPlayground selectedEndpoint={selectedEndpoint} />
      </div>
    </div>
  );
};

export default ApiDocsPage;
